import { useEffect, useContext } from "react";
import { useCookies } from "react-cookie";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import ReactGA from "react-ga4"; // Importa react-ga4

// context
import { SignedInContext } from "./AppEnv";

// pages
import Error from "./error/Error";
import Login from "./auth/Login";
import LoggedInWrapper, { MENU_KEYS } from "./LoggedInWrapper";

// utils
import { SignedInDoctor, UnloggedDoctor } from "./auth/interfaces";
import { getAdminMe, getMe, isLogged } from "./auth/utils";
import {
  ADMIN_HOME_PATH,
  ADMIN_LOGIN_TOKEN,
  ADMIN_PATH,
  COMPARE_EXAMS_PATH,
  HOME_PATH,
  INTERNAL_ERR,
  LOGIN_PATH,
  LOGIN_TOKEN,
  PATIENTS_PATH,
  PDF_EXAMS_PATH,
  REGISTER_PATH,
  SESS_EXPIRED,
} from "./env";
import Home from "./home/Home";
import HomeHeader from "./home/HomeHeader";
import PatientsHeader from "./patients/PatientsHeader";
import PatientsRegistry from "./patients/PatientsRegistry";
import StudioAdminHeader from "./studio/StudioAdminHeader";
import StudioAdmin from "./studio/StudioAdmin";
import DoctorSheetHeader from "./doctor/DoctorSheetHeader";
import DoctorSheet from "./doctor/DoctorSheet";
import PatientSheet from "./patients/PatientSheet";
import PosturalExamHeader from "./exams/PosturalExamHeader";
import PosturalExamWrapper from "./exams/PosturalExamWrapper";
import ExamPDF from "./exams/ExamPDF";
import AdminLogin from "./auth/AdminLogin";
import AdminHome from "./home/AdminHome";
import FakeLoggedInWrapper from "./FakeLoggedInWrapper";
import TutorialInfo from "./tutorial/TutorialInfo";
import TutorialHeader from "./tutorial/TutorialHeader";
import CalendarHeader from "./calendar/CalendarHeader";
import CalendarRegistry from "./calendar/CalendarRegistry";
import { Calendar } from "react-big-calendar";
import CalendarRegistery from "./calendar/CalendarRegistry";

import PriceInfo from "./price/PriceInfo";
import PriceHeader from "./price/PriceHeader";
import SubscriptionStudio from "./subscription/SubscriptionStudio";
import ExamCompare from "./exams/ExamCompare";
import PhysioChat from "./chat/PhysioChat";

const PhysioRouter = () => {
  const signedInDoctor: SignedInDoctor =
    useContext(SignedInContext).signedInDoctor;
  const setSignedInDoctor = useContext(SignedInContext).setSignedInDoctor;

  const [cookies, setCookie] = useCookies([LOGIN_TOKEN, ADMIN_LOGIN_TOKEN]);

  const navigate = useNavigate();
  const location = useLocation(); // Usato per tracciare le route
  const inLogin: boolean = useLocation().pathname.indexOf("login") >= 0;

  const trackingId = "G-PNGXVEWN6R"; // Inserisci qui il tuo ID di monitoraggio

  useEffect(() => {
    // Inizializza Google Analytics
    ReactGA.initialize(trackingId);
  }, []);

  useEffect(() => {
    // Invia l'evento di visualizzazione della pagina a Google Analytics
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]); // Esegui il tracking quando cambia la location

  const checkIfLogged = async () => {
    // Rimuovi il controllo dell'autenticazione per la pagina SubscriptionStudio
    if (window.location.href.indexOf(REGISTER_PATH) >= 0) return;
    //console.log("Checking if logged in ", signedInDoctor);
    try {
      const loggedIn = await isLogged(signedInDoctor);
      if (loggedIn && inLogin) navigate("/app" + HOME_PATH);
      else if (!loggedIn && setSignedInDoctor) {
        if (cookies[LOGIN_TOKEN] != undefined) {
          //token present
          getMe(cookies[LOGIN_TOKEN])
            .then((loggedInDoc: SignedInDoctor) => {
              if (loggedInDoc.id >= 0) setSignedInDoctor(loggedInDoc);
              else {
                setSignedInDoctor(UnloggedDoctor);
                setCookie(LOGIN_TOKEN, undefined, { path: "/" });
                navigate("/app" + LOGIN_PATH + "?" + SESS_EXPIRED + "=1");
              }
            })
            .catch(() =>
              navigate("/app" + LOGIN_PATH + "?" + INTERNAL_ERR + "=1")
            );
        } else if (!inLogin)
          navigate("/app" + LOGIN_PATH + "?" + SESS_EXPIRED + "=1");
      }
    } catch (err) {
      navigate("/app" + LOGIN_PATH + "?" + INTERNAL_ERR + "=1");
    }
  };

  const checkIfAdminLogged = async () => {
    // Rimuovi il controllo dell'autenticazione per la pagina SubscriptionStudio
    if (window.location.href.indexOf(REGISTER_PATH) >= 0) return;
    //console.log("Checking if ADMIN logged in ", signedInDoctor);
    try {
      const admin = await getAdminMe(cookies[ADMIN_LOGIN_TOKEN]);
      //console.log(admin);
      if (admin.id == -12 && signedInDoctor.id != -12) setSignedInDoctor(admin);
      else if (admin.id != -12) {
        setSignedInDoctor(UnloggedDoctor);
        setCookie(ADMIN_LOGIN_TOKEN, undefined, { path: "/" });
        navigate("/app" + LOGIN_PATH + "?" + SESS_EXPIRED + "=1");
      }
      if (admin.id === -111)
        navigate("/app" + LOGIN_PATH + "?" + SESS_EXPIRED + "=1");
    } catch (err) {
      navigate("/app" + LOGIN_PATH + "?" + INTERNAL_ERR + "=1");
    }
  };

  useEffect(() => {
    if (
      window.location.href.indexOf(ADMIN_PATH) == -1 &&
      window.location.href.indexOf(ADMIN_HOME_PATH) == -1
    )
      checkIfLogged();
    else if (window.location.href.indexOf(ADMIN_HOME_PATH) >= 0) {
      checkIfAdminLogged();
    }
  }, [signedInDoctor, navigate]);

  return (


    
    <Routes>
      <Route path={"/app" + LOGIN_PATH} element={<Login />} />
      <Route path={"/app" + ADMIN_PATH} element={<AdminLogin />} />
      <Route path={"/app" + REGISTER_PATH} element={<SubscriptionStudio />} />
    
      <Route
        path={"/app" + ADMIN_HOME_PATH}
        element={
          cookies[ADMIN_LOGIN_TOKEN] && (
            <LoggedInWrapper
              selectedMenuItem={MENU_KEYS.HOME}
              selectedHeader={<HomeHeader />}
              selectedContent={<AdminHome />}
              hiddenSider={true}
              adminPanel={true}
            ></LoggedInWrapper>
          )
        }
      />
      <Route
        path={"/app" + HOME_PATH}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.HOME}
            selectedHeader={<HomeHeader />}
            selectedContent={<Home />}
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + HOME_PATH + "/" + MENU_KEYS.PATIENTS_REGISTRY}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.PATIENTS_REGISTRY}
            selectedHeader={<PatientsHeader title="Elenco Pazienti" />}
            selectedContent={<PatientsRegistry />}
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + HOME_PATH + "/" + MENU_KEYS.DOC}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.DOC}
            selectedHeader={<DoctorSheetHeader />}
            selectedContent={
              signedInDoctor.studioMaster ? <DoctorSheet /> : <></>
            }
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + HOME_PATH + "/" + MENU_KEYS.TUTO}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.TUTO}
            selectedHeader={<TutorialHeader />}
            selectedContent={
              signedInDoctor.studioMaster ? <TutorialInfo /> : <></>
            }
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + HOME_PATH + "/" + MENU_KEYS.LPRICE}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.LPRICE}
            selectedHeader={<PriceHeader />}
            selectedContent={
              signedInDoctor.studioMaster ? <PriceInfo /> : <></>
            }
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + HOME_PATH + "/" + MENU_KEYS.CALE}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.CALE}
            selectedHeader={<CalendarHeader />}
            selectedContent={
              signedInDoctor.studioMaster ? <CalendarRegistery /> : <></>
            }
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + HOME_PATH + "/" + MENU_KEYS.STUDIO_ADMIN}
        element={
          <LoggedInWrapper
            selectedMenuItem={MENU_KEYS.STUDIO_ADMIN}
            selectedHeader={<StudioAdminHeader />}
            selectedContent={
              signedInDoctor.studioMaster ? <StudioAdmin /> : <></>
            }
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + PATIENTS_PATH + "/:id"}
        element={
          <LoggedInWrapper
            selectedHeader={<PatientsHeader title="Scheda Paziente" />}
            selectedContent={<PatientSheet />}
          ></LoggedInWrapper>
        }
      />
      
  
      <Route
        path={"/app" + PDF_EXAMS_PATH + "/:id" + "/multiple_exams/:examsIds"}
        element={
          <LoggedInWrapper
            selectedHeader={<PosturalExamHeader title="Esame posturale PDF" />}
            selectedContent={<ExamPDF />}
            hiddenSider={true}
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + PATIENTS_PATH + "/:id" + "/:examId"}
        element={
          <LoggedInWrapper
            selectedHeader={<PosturalExamHeader title="Esame posturale" />}
            selectedContent={<PosturalExamWrapper />}
            hiddenSider={true}
          ></LoggedInWrapper>
        }
      />
      <Route
        path={"/app" + PDF_EXAMS_PATH + "/:id" + "/:examId"}
        element={
          <LoggedInWrapper
            selectedHeader={<PosturalExamHeader title="Esame posturale PDF" />}
            selectedContent={<ExamPDF />}
            hiddenSider={true}
          ></LoggedInWrapper>
        }
      />
        <Route
        path={"/app" + COMPARE_EXAMS_PATH + "/:id" + "/multiple_exams/:examsIds"}
        element={
          <LoggedInWrapper
            selectedHeader={<PosturalExamHeader title="Comparazione Esami" />}
            selectedContent={<ExamCompare />}
            hiddenSider={true}
          ></LoggedInWrapper>
        }
      />
      <Route path="*" element={<Error />} />
    </Routes>
   
  
  );
};

export default PhysioRouter;
